import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Button,
    ButtonGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Checkbox,
    Alert,
    TextField,
    IconButton,
    Stack,
} from "@mui/material";
import { Add, Delete, Edit, Save } from "@mui/icons-material";

import {
    createLoopLayerAction,
    deleteLoopLayerAction,
} from "../../../../store/actions/loopActions";
import { poster, deleter } from "../../../../utils/axios";
import { toast } from "react-toastify";
import ConfirmDeleteDialog from "../../../ConfirmDeleteDialog";
import { updateProject } from "../../../../store/slices/projectsSlice";
import ImageDialog from "../../../CustomComponents/ImageDialog";
import PropTypes from "prop-types";

export default function LoopLayerSelector({ loopId, editable = true }) {
    const dispatch = useDispatch();
    const { loopLayers } = useSelector((state) => state.loop);
    const { potential_layers: layerOptions, ...project } = useSelector((state) => state.projects.selectedProject);
    
    const [customLayer, setCustomLayer] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    
    const selectedLayers = useMemo(() => {
        const selected = new Set;
        loopLayers.forEach(ll => {
            selected.add(ll.layer);
        });
        return selected;
    }, [loopLayers]);

    const toggleLayerSelection = (layerId, isChecked) => {
        if (!editable) return;

        if (isChecked) {
            dispatch(createLoopLayerAction({
                loop: loopId,
                layer: layerId,
            }));
        } else {
            const loopLayer = loopLayers.find((ll) => ll.layer === layerId);
            if (loopLayer) {
                dispatch(deleteLoopLayerAction(loopLayer.id));
            }
        }
    };

    const handleCheckbox = (event) => {
        const layerId = Number(event.target.name);
        const isChecked = event.target.checked;
        toggleLayerSelection(layerId, isChecked);
    };

    const handleRowClick = (layerId) => {
        if (!editable) return;
        const currentlySelected = selectedLayers.has(layerId);
        toggleLayerSelection(layerId, !currentlySelected);
    };

    const handleAddLayer = () => {
        setCustomLayer({ name: "", description: "" });
    };

    const handleEditCustomLayer = (id) => {
        setCustomLayer(layerOptions.find((layer) => layer.id === id));
    };

    const saveCustomLayer = () => {
        poster(`/projects/${project.id}/save_custom_layer/`, customLayer).then((response) => {
            if (response.status === 200) {
                const newLayerOptions = [...layerOptions.filter((layer) => layer.id !== response.data.id), response.data];
                dispatch(
                    updateProject({
                        ...project,
                        potential_layers: newLayerOptions,
                    })
                );
                dispatch(createLoopLayerAction({
                    loop: loopId,
                    layer: response.data.id
                }   ));
                setCustomLayer(false);
                toast.success("Onderdeel opgeslagen");
            }
        });
    };

    const handleDeleteCustomLayer = (id) => {
        deleter(`/projects/${project.id}/delete_custom_layer/${id}/`).then((response) => {
            if (response.status === 200) {
                const newLayerOptions = layerOptions.filter((layer) => layer.id !== id);
                dispatch(
                    updateProject({
                        ...project,
                        potential_layers: newLayerOptions,
                    })
                );
                setDeleteDialogOpen(false);
                toast.success("Onderdeel verwijderd");
            }
        });
    };

    // Loading is intentionally not used for row toggles to avoid flicker

    return (
        <Box>
            <ConfirmDeleteDialog
                open={deleteDialogOpen !== false}
                onClose={() => setDeleteDialogOpen(false)}
                dialogTitle="Onderdeel verwijderen"
                dialogContentText="Weet u zeker dat u dit onderdeel wilt verwijderen?"
                handleConfirm={() => handleDeleteCustomLayer(deleteDialogOpen)}
                dialogConfirmButton="Verwijderen"
            />
            <ImageDialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                imageSrc="/infoGraphicLayers.png"
                alt="Infographic layers"
            />
            <Box display="flex" flexDirection="column" mb={2}>
                <Typography variant="h6" component="h2">
                    Onderdelen
                </Typography>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Typography variant="body2" color="text.secondary">
                        Selecteer hier de onderdelen die in de dijk zitten. Het is mogelijk om extra onderdelen toe te voegen.  
                    </Typography>
                    <Box
                        component="img"
                        src="/infoGraphicLayers.png"
                        alt="Infographic layers"
                        onClick={() => setImageDialogOpen(true)}
                        sx={{
                            maxWidth: { xs: '200px', md: '300px' },
                            height: 'auto',
                            flexShrink: 0,
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8,
                            },
                        }}
                    />
                </Stack>
            </Box>

            {layerOptions.length === 0 ? (
                <Alert severity="info">
                    Geen onderdelen beschikbaar voor dit project.
                </Alert>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography fontWeight={"bold"}>Algemene onderdelen</Typography>
                                </TableCell>
                            </TableRow>
                            {layerOptions
                                .filter((layer) => layer.project === null)
                                .map((layer) => {
                                    const selected = selectedLayers.has(layer.id);
                                    return (
                                    <TableRow
                                        key={layer.id}
                                        hover={editable}
                                        onClick={editable ? () => handleRowClick(layer.id) : undefined}
                                        sx={{ cursor: editable ? 'pointer' : 'default' }}
                                    >
                                        <TableCell>
                                            <Typography>{layer.name}</Typography>
                                        </TableCell>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                name={layer.id.toString()}
                                                checked={selected}
                                                disabled={!editable}
                                                onChange={handleCheckbox}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography>{layer.description}</Typography>
                                        </TableCell>
                                    </TableRow>
                                )})}
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography fontWeight={"bold"}>Project-specifieke onderdelen</Typography>
                                </TableCell>
                            </TableRow>
                            {layerOptions
                                .filter((layer) => layer.project !== null  && layer.id !== customLayer?.id)
                                .map((layer) => {
                                    const selected = selectedLayers.has(layer.id);
                                    return (
                                    <TableRow
                                        key={layer.id}
                                        hover={editable}
                                        onClick={editable ? () => handleRowClick(layer.id) : undefined}
                                        sx={{ cursor: editable ? 'pointer' : 'default' }}
                                    >
                                        <TableCell>
                                            <Typography>{layer.name}</Typography>
                                        </TableCell>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                name={layer.id.toString()}
                                                checked={selected}
                                                disabled={!editable}
                                                onChange={handleCheckbox}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={2}>
                                                <Typography>{layer.description}</Typography>
                                                {editable && (
                                                    <ButtonGroup>
                                                        <IconButton sx={{ p: 0, m: 0 }} onClick={(e) => { e.stopPropagation(); handleEditCustomLayer(layer.id); }}>
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                        <IconButton sx={{ p: 0, m: 0 }} onClick={(e) => { e.stopPropagation(); setDeleteDialogOpen(layer.id); }}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </ButtonGroup>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                )})}
                            {customLayer && (
                                <TableRow key={"customlayer"}>
                                    <TableCell>
                                        <Stack direction="row">
                                            <TextField
                                                autoFocus
                                                value={customLayer.name}
                                                onChange={(e) => setCustomLayer({ ...customLayer, name: e.target.value })}
                                                label="Onderdeel"
                                            />
                                            <IconButton onClick={saveCustomLayer} disabled={customLayer?.name === ""}>
                                                <Save />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={true} disabled={true} onChange={() => setCustomLayer(false)} />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row">
                                            <TextField
                                                value={customLayer.description}
                                                onChange={(e) => setCustomLayer({ ...customLayer, description: e.target.value })}
                                                label="Omschrijving"
                                            />
                                            <IconButton onClick={saveCustomLayer} disabled={customLayer?.name === "" || !editable}>
                                                <Save />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Button onClick={handleAddLayer} disabled={!editable}>
                                        <Add />
                                        Onderdeel toevoegen
                                    </Button>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
} 

LoopLayerSelector.propTypes = {
    loopId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    editable: PropTypes.bool,
};